import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userAPI, authAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { User, Edit3, Save, X, ArrowLeft, Lock, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './Profile.css';

function Profile() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { updateUser } = useAuthStore();
  const [editing, setEditing] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    bio: '',
    profile_photo_url: ''
  });

  const { data: profile, isLoading, isError, error } = useQuery({
    queryKey: ['profile'],
    queryFn: () => userAPI.getProfile().then(res => res.data.user),
    retry: 1
  });

  useEffect(() => {
    if (profile) {
      setForm({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        phone: profile.phone || '',
        bio: profile.bio || '',
        profile_photo_url: profile.profile_photo_url || ''
      });
    }
  }, [profile]);

  const updateMutation = useMutation({
    mutationFn: (data) => userAPI.updateProfile(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      if (res.data.user) {
        updateUser(res.data.user);
      }
      setEditing(false);
      setSuccessMsg('Profile updated successfully!');
      setTimeout(() => setSuccessMsg(''), 3000);
    }
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    updateMutation.mutate(form);
  };

  const handleCancel = () => {
    setEditing(false);
    if (profile) {
      setForm({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        phone: profile.phone || '',
        bio: profile.bio || '',
        profile_photo_url: profile.profile_photo_url || ''
      });
    }
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    if (!passwordForm.current_password || !passwordForm.new_password) {
      setPasswordError('All fields are required');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError('New passwords do not match');
      return;
    }

    setPasswordLoading(true);
    try {
      await authAPI.changePassword(passwordForm.current_password, passwordForm.new_password);
      setPasswordSuccess('Password changed successfully!');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      setTimeout(() => {
        setPasswordSuccess('');
        setShowPasswordSection(false);
      }, 3000);
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  if (isLoading) return <div className="profile-loading">Loading profile...</div>;

  if (isError || !profile) {
    return (
      <div className="profile-page">
        <h1><User size={28} /> My Profile</h1>
        <div className="profile-card" style={{ padding: 32, textAlign: 'center' }}>
          <p style={{ color: '#dc2626', marginBottom: 16 }}>
            {isError ? (error?.response?.data?.error || 'Failed to load profile. Please try again.') : 'Profile data not available.'}
          </p>
          <button className="btn-edit-profile" onClick={() => navigate('/')} style={{ margin: '0 auto' }}>
            <ArrowLeft size={16} /> Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const initials = `${(profile.first_name || '?')[0]}${(profile.last_name || '?')[0]}`.toUpperCase();

  return (
    <div className="profile-page">
      <h1><User size={28} /> My Profile</h1>

      {successMsg && <div className="profile-success">{successMsg}</div>}

      <div className="profile-card">
        <div className="profile-banner">
          <div className="profile-avatar">{initials}</div>
        </div>
        <div className="profile-content">
          <div className="profile-header-row">
            <div>
              <div className="profile-name">{profile?.first_name} {profile?.last_name}</div>
              <div className="profile-email">{profile?.email}</div>
              <div className="profile-badges">
                <span className="profile-badge role">{profile?.role}</span>
                <span className="profile-badge tier">{profile?.tier}</span>
                <span className={`profile-badge status-${profile?.status}`}>{profile?.status}</span>
              </div>
            </div>
            {!editing && (
              <button className="btn-edit-profile" onClick={() => setEditing(true)}>
                <Edit3 size={16} /> Edit Profile
              </button>
            )}
          </div>

          <div className="profile-stats">
            <div className="profile-stat">
              <div className="stat-val">{profile?.points || 0}</div>
              <div className="stat-lbl">Points</div>
            </div>
            <div className="profile-stat">
              <div className="stat-val">{profile?.tier}</div>
              <div className="stat-lbl">Tier</div>
            </div>
            <div className="profile-stat">
              <div className="stat-val">${Number(profile?.total_earnings ?? 0).toFixed(2)}</div>
              <div className="stat-lbl">Earnings</div>
            </div>
            <div className="profile-stat">
              <div className="stat-val">{Number((profile?.commission_rate ?? 0) * 100).toFixed(0)}%</div>
              <div className="stat-lbl">Commission</div>
            </div>
          </div>

          {editing ? (
            <div className="profile-edit-form">
              <div className="profile-form-row">
                <div className="profile-form-group">
                  <label>First Name</label>
                  <input name="first_name" value={form.first_name} onChange={handleChange} placeholder="First name" />
                </div>
                <div className="profile-form-group">
                  <label>Last Name</label>
                  <input name="last_name" value={form.last_name} onChange={handleChange} placeholder="Last name" />
                </div>
              </div>
              <div className="profile-form-row">
                <div className="profile-form-group">
                  <label>Phone</label>
                  <input name="phone" value={form.phone} onChange={handleChange} placeholder="Phone number" />
                </div>
                <div className="profile-form-group">
                  <label>Photo URL</label>
                  <input name="profile_photo_url" value={form.profile_photo_url} onChange={handleChange} placeholder="https://..." />
                </div>
              </div>
              <div className="profile-form-group">
                <label>Bio</label>
                <textarea name="bio" value={form.bio} onChange={handleChange} placeholder="Tell us about yourself..." />
              </div>
              {updateMutation.isError && (
                <p style={{ color: '#dc2626', fontSize: 14 }}>
                  {updateMutation.error?.response?.data?.error || 'Failed to update. Try again.'}
                </p>
              )}
              <div className="profile-form-actions">
                <button className="btn-cancel" onClick={handleCancel}><X size={16} /> Cancel</button>
                <button className="btn-save" onClick={handleSave} disabled={updateMutation.isPending}>
                  <Save size={16} /> {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          ) : (
            <div className="profile-info">
              <div className="info-item">
                <label>First Name</label>
                <div className="info-value">{profile?.first_name || '—'}</div>
              </div>
              <div className="info-item">
                <label>Last Name</label>
                <div className="info-value">{profile?.last_name || '—'}</div>
              </div>
              <div className="info-item">
                <label>Phone</label>
                <div className="info-value">{profile?.phone || '—'}</div>
              </div>
              <div className="info-item">
                <label>Member Since</label>
                <div className="info-value">{profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : '—'}</div>
              </div>
              {profile?.bio && (
                <div className="info-item full-width">
                  <label>Bio</label>
                  <div className="info-value">{profile.bio}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Change Password Section */}
      <div className="profile-card" style={{ marginTop: 24 }}>
        <div className="profile-content" style={{ padding: '24px 32px' }}>
          <div className="profile-header-row">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Lock size={20} />
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Password & Security</h3>
            </div>
            {!showPasswordSection && (
              <button className="btn-edit-profile" onClick={() => { setShowPasswordSection(true); setPasswordError(''); setPasswordSuccess(''); }}>
                <Lock size={16} /> Change Password
              </button>
            )}
          </div>

          {showPasswordSection && (
            <div className="profile-edit-form" style={{ marginTop: 20 }}>
              {passwordError && <div className="profile-error-msg">{passwordError}</div>}
              {passwordSuccess && <div className="profile-success">{passwordSuccess}</div>}

              <div className="profile-form-group">
                <label>Current Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showCurrentPw ? 'text' : 'password'}
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                    placeholder="Enter current password"
                  />
                  <button type="button" className="password-toggle-btn" onClick={() => setShowCurrentPw(!showCurrentPw)}>
                    {showCurrentPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="profile-form-row">
                <div className="profile-form-group">
                  <label>New Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showNewPw ? 'text' : 'password'}
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                      placeholder="At least 8 characters"
                    />
                    <button type="button" className="password-toggle-btn" onClick={() => setShowNewPw(!showNewPw)}>
                      {showNewPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div className="profile-form-group">
                  <label>Confirm New Password</label>
                  <input
                    type="password"
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                    placeholder="Confirm new password"
                  />
                </div>
              </div>

              {passwordForm.new_password && passwordForm.new_password.length >= 8 && (
                <div className="password-strength-indicator">
                  <div className={`strength-bar-fill ${passwordForm.new_password.length >= 12 ? 'strong' : 'medium'}`}></div>
                  <span>{passwordForm.new_password.length >= 12 ? 'Strong password' : 'Good password'}</span>
                </div>
              )}

              <div className="profile-form-actions">
                <button className="btn-cancel" onClick={() => { setShowPasswordSection(false); setPasswordError(''); setPasswordForm({ current_password: '', new_password: '', confirm_password: '' }); }}>
                  <X size={16} /> Cancel
                </button>
                <button className="btn-save" onClick={handleChangePassword} disabled={passwordLoading}>
                  <Lock size={16} /> {passwordLoading ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </div>
          )}

          {!showPasswordSection && (
            <p style={{ color: '#6b7280', fontSize: 14, marginTop: 12 }}>
              It's a good idea to use a strong password that you're not using elsewhere.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Profile;
